/**
 * Announcer — a single global `role="status" aria-live="polite"` region
 * mounted once at the app shell. Components fire concise updates by
 * calling `announce("message")` exported from this module, and screen
 * readers will read the new text without moving focus.
 *
 * Used for:
 *   • Calculator result announcements ("Estimated repair exposure: $4,200")
 *   • Upload status ("Upload complete. Generating preview…")
 *   • Form-level validation summaries
 *
 * Why a single region instead of one per component? Because multiple
 * polite live regions on the same page can drown each other out and
 * are inconsistently supported across NVDA / JAWS / VoiceOver. One
 * canonical region, owned by the app shell, is the most reliable path.
 *
 * Companion to the existing toast (sonner) — toasts are visual,
 * announcements are auditory. They do not duplicate each other.
 */
import { useEffect, useState } from "react";

let _setText = null;
let _queue = [];

export function announce(message) {
    if (!message) return;
    if (_setText) {
        // Clear first so identical consecutive messages still trigger.
        _setText("");
        setTimeout(() => _setText && _setText(String(message)), 50);
    } else {
        _queue.push(String(message));
    }
}

export default function Announcer() {
    const [text, setText] = useState("");

    useEffect(() => {
        _setText = setText;
        if (_queue.length) {
            const flush = _queue.slice();
            _queue = [];
            flush.forEach((m) => announce(m));
        }
        return () => {
            _setText = null;
        };
    }, []);

    return (
        <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            data-testid="aria-live-announcer"
        >
            {text}
        </div>
    );
}
